// ============================================================
// ConditionNumberPicker - the duration / level / severity / intensity row
// ============================================================
// Shown inline under a category's badges once a numbered condition is
// pressed. What the number MEANS (and so the caption and presets) comes
// from `numberPickerCopy`; the state lives in `useConditionNumberPicker`.

import { rollHallucinationIntensity, defaultHallucinogen } from '../engine/hallucination';
import type { EligibleCondition } from '../engine/conditionEligibility';
import { holdCaption, numberPickerCopy } from './conditionPicker';
import type { ConditionNumberPicker as PickerState } from './useConditionNumberPicker';
import styles from '../styles/AddConditionModal.module.css';

interface ConditionNumberPickerProps {
  cond: EligibleCondition;
  picker: PickerState;
}

export function ConditionNumberPicker({ cond, picker }: ConditionNumberPickerProps) {
  const copy = numberPickerCopy(cond.durationUnit);
  const confirmCustom = () => picker.add(cond, Math.max(1, parseInt(picker.custom, 10) || 1));
  // ROLL: 1d10 through the engine's dice - the default profile's die, no bonus, no clamp.
  const roll = () => picker.add(cond, rollHallucinationIntensity(defaultHallucinogen()));
  return (
    <div className={styles.subPanel} role="group" aria-label={copy.groupLabel}>
      <div className={styles.subPanelLabel}>{copy.caption}</div>
      <div className={styles.presetRow}>
        {copy.presets.map((n) => (
          <button key={n} className={styles.presetBtn} onClick={() => picker.add(cond, n)} type="button">
            {n}
          </button>
        ))}
        {copy.isIntensity && (
          <button className={styles.presetBtn} onClick={roll} type="button" title="Roll 1d10">
            ROLL
          </button>
        )}
        {copy.offersPermanent && <PermanentButton code={cond.code} onPick={() => picker.add(cond, 0)} />}
        <input
          className={styles.customInput}
          type="number"
          min={1}
          value={picker.custom}
          placeholder="#"
          onChange={(e) => picker.setCustom(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && confirmCustom()}
          aria-label={copy.customLabel}
        />
        {picker.custom && (
          <button className={styles.customInputConfirm} onClick={confirmCustom} type="button">
            Add
          </button>
        )}
      </div>
      {copy.isIntensity && <HoldRow holdRounds={picker.holdRounds} onChange={picker.setHoldRounds} />}
    </div>
  );
}

/** "No countdown": Dying without a death clock reads as untreated, not permanent (v6.34.5). */
function PermanentButton({ code, onPick }: { code: string; onPick: () => void }) {
  const dying = code === 'Dy';
  return (
    <button
      className={styles.presetBtn}
      onClick={onPick}
      type="button"
      title={
        dying ? 'Dying with no death clock - dies without medical treatment' : 'No countdown - stays until removed'
      }
    >
      {dying ? 'No timer (untreated)' : 'Permanent'}
    </button>
  );
}

/** Hallucinating's hold: blank steps down a rung a round, N holds N rounds, 0 until removed. */
function HoldRow({ holdRounds, onChange }: { holdRounds: string; onChange: (value: string) => void }) {
  return (
    <div className={styles.presetRow}>
      <span className={styles.subPanelLabel}>{holdCaption(holdRounds)}</span>
      <input
        className={styles.customInput}
        type="number"
        min={0}
        value={holdRounds}
        placeholder="#"
        onChange={(e) => onChange(e.target.value)}
        aria-label="Hold rounds"
      />
      <span className={styles.subPanelLabel}>rounds (0 = until removed)</span>
    </div>
  );
}
